import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { Avatar, Menu, MenuItem, MenuTrigger } from '@backstage/ui';
import { ChevronRightIcon, GearIcon, LogOutIcon, UserIcon } from './Icons';
import styles from './Sidebar.module.css';

interface Profile {
  displayName: string;
  email?: string;
  picture?: string;
}

/**
 * Footer user button (avatar + name) opening a BUI menu with
 * Profile / Settings / Sign out. Settings moves here from the old
 * sidebar's SidebarGroup.
 */
export function UserMenu({ settingsHref }: { settingsHref: string }) {
  const identityApi = useApi(identityApiRef);
  const [profile, setProfile] = useState<Profile | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    identityApi.getProfileInfo().then(info => {
      if (!cancelled) {
        setProfile({
          displayName: info.displayName || 'User',
          email: info.email,
          picture: info.picture,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const signOut = () => {
    identityApi.signOut().finally(() => {
      window.location.href = '/';
    });
  };

  const displayName = profile?.displayName ?? 'User';

  return (
    <MenuTrigger>
      <button type="button" className={styles.user} aria-label="User menu">
        <Avatar
          src={profile?.picture ?? ''}
          name={displayName}
          className={styles.userAvatar}
        />
        <span className={styles.who}>
          <b>{displayName}</b>
          <span>{profile?.email ?? ''}</span>
        </span>
        <span className={styles.chev}>
          <ChevronRightIcon size={15} />
        </span>
      </button>
      <Menu onAction={key => key === 'signout' && signOut()}>
        <MenuItem id="profile" href={settingsHref}>
          <UserIcon size={16} />
          Profile
        </MenuItem>
        <MenuItem id="settings" href={settingsHref}>
          <GearIcon size={16} />
          Settings
        </MenuItem>
        <MenuItem id="signout" className={styles.menuDanger}>
          <LogOutIcon size={16} />
          Sign out
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}
